// src/pages/POS/components/PosTable.jsx
import React, { useEffect, useRef, useState } from "react";
import "./posTable.css";
import { Table, Button, Popconfirm, Flex, Input, Modal } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InputNumberField from "../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  addItemRow,
  deleteItemRow,
  updateItemRow,
  calculateTotals,
  setFooterValues,
  setPayCart,
} from "../posSlice";
import SharedFooter from "../../../components/DataTableFooter/SharedFooter";
import { beepSuccess, beepError } from "../../../utils/beep";
import { fetchItems } from "../../../store/commonThunks/lookups";
import { selectItems } from "../../../store/commonSelectors/lookupsSelectors";
import { fetchCustomerSpecialPrice, fetchItemStock } from "../posThunk";
import Big from "big.js";
import Swal from "sweetalert2";

const PosTable = ({ authInfo }) => {
  const [highlightRow, setHighlightRow] = useState(null);
  const [serialModal, setSerialModal] = useState({ open: false, row: null });
  const [serialInput, setSerialInput] = useState([]);

  const qtyRefs = useRef({});
  const rateRefs = useRef({});
  const discountRefs = useRef({});
  const productRefs = useRef({});
const barcodeBuffer = useRef("");
const barcodeTimer = useRef(null);
  const dispatch = useDispatch();
  const { customer, footerValues, itemCart, taxMode, cartMode } = useSelector(
    (s) => s.pos
  );

  const items = useSelector(selectItems);
const isIndia = authInfo?.country?.toLowerCase() === "india";
const taxLabel = isIndia ? "GST" : "VAT";
const gstEnabled = authInfo?.gst_enabled === "yes";
const showTax = !isIndia || gstEnabled;
  const handleUpdate = (rowKey, field, value) => {
    dispatch(updateItemRow({ key: rowKey, updates: { [field]: value } }));
    dispatch(calculateTotals({ authInfo }));
  };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };
    if (field === "payCart") {
      const newPaidAmount = value
      .reduce((sum, item) => sum.plus(item.tran_amount || 0), new Big(0))
      .toNumber();

      updatedFooter = {
        ...updatedFooter,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };

      // ✅ normalize structure before saving
      const normalizedCart = value.map((v) => ({
        acc_type_id: v.acc_type_id,
        to_acc_id: v.to_acc_id || 0,
        to_acc_name: v.to_acc_name || "",
        tran_amount: String(v.tran_amount || 0),
      }));

      dispatch(setPayCart(normalizedCart));
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    dispatch(calculateTotals({ authInfo }));
  };

  const handleAddRow = () => {
    const newRow = {
      key: Date.now() + Math.random(),
      item_id: null,
      product: "",
      qty: 1,
      rate: 0,
      discount: 0,
      tax_per: 0,
      unit_symbol: "",
    };
    dispatch(addItemRow(newRow));
    dispatch(calculateTotals({ authInfo }));
  };

  const addItemFromBarcode = (item, record) => {

      itemCart.forEach((r) => {
    if (r.isBlank && r.item_id === null && r.product !== "") {
      dispatch(updateItemRow({
        key: r.key,
        updates: { product: "" },
      }));
    }
  });
    if (!item) {
      beepError();
      Swal.fire("Item Not Found", `${record.product || ""}`, "warning");
      return;
    }


    // 🔥 SAME ROW re-select (refill cleared row)
    if (record.item_id === null && record.product === "") {
      dispatch(
        updateItemRow({
          key: record.key,
          updates: {
            item_id: item.item_id,
            product: item.item_name,
            qty: 1,
            rate: Number(item.sale_rate || 0).toFixed(2),
            unit_symbol: item.unit_symbol,
            discount: item.discount || 0,
            tax_per: item.tax_per || 0,
            isBlank: false,
          },
        })
      );

      dispatch(calculateTotals({ authInfo }));
      beepSuccess();
      flashRow(record.key);
      return;
    }


    // ✅ If item already exists, just increase qty
    const existing = itemCart.find((r) => r.item_id === item.item_id);
    // const existing = itemCart.find((r) => r.item_id === item.item_id && r.key !== record.key);

    if (existing) {
      dispatch(
        updateItemRow({
          key: existing.key,
          updates: { qty: Number(existing.qty || 1) + 1 },
        })
      );

      dispatch(calculateTotals({ authInfo }));
      beepSuccess();
      flashRow(existing.key);

      // focus qty field
      // setTimeout(() => qtyRefs.current[existing.key]?.focus(), 120);
      return;
    }

    // ✅ If new item → check stock first
    dispatch(
      fetchItemStock({
        itemId: item.item_id,
        branchId: authInfo.branch_id,
      })
    ).then((res) => {
      const stock = Number(res?.payload?.[0]?.current_qty || 0);
  
  dispatch(
    updateItemRow({
      key: record.key,
      updates: {
        itemInfo: { current_qty: stock }, 
      },
    })
  );
      // if (stock <= 0) {
      //   beepError();
      //   Swal.fire("No Stock Available!", "", "warning");
      //   return;
      // }

      // ✅ Set values in row
      dispatch(
        updateItemRow({
          key: record.key,
          updates: {
            item_id: item.item_id,
            product: item.item_name,
            qty: 1,
            rate: Number(item.sale_rate || 0).toFixed(2),
            unit_symbol: item.unit_symbol,
            discount: item.discount || 0,
            tax_per: item.tax_per || 0,
            item_code: item.item_code || "",
            warehouse_id: item.warehouse_id || 0,
            conversion: item.conversion || 1,
            per_conversion: item.conversion || 1,
            base_unit_name: item.base_unit_name || "",
            isBlank: false,
          },
        })
      );

      // ✅ Customer special price
      dispatch(
        fetchCustomerSpecialPrice({
          customerId: customer?.acc_id,
          itemId: item.item_id,
        })
      ).then((sp) => {
        const special = sp?.payload?.special_price;
        if (special) {
          dispatch(
            updateItemRow({
              key: record.key,
              updates: { rate: Number(special).toFixed(2) },
            })
          );
        }

        dispatch(calculateTotals({ authInfo }));
      });

      beepSuccess();
      flashRow(record.key);

      // ✅ Auto add new empty row
      if (cartMode === "auto" && record.isBlank) {
        dispatch(
          addItemRow({
            key: Date.now() + Math.random(),
            product: "",
            qty: null,
            rate: null,
            discount: 0,
            tax_per: 0,
            unit_symbol: "",
            isBlank: true,
          })
        );
      }

      // ✅ Focus qty input for newly added row
      // setTimeout(() => qtyRefs.current[record.key]?.focus(), 120);
    });
  };
useEffect(() => {
  if (!items.length) {
    dispatch(fetchItems());
  }
}, [dispatch,items.length]);

  const openSerialModal = (row) => {
    setSerialModal({ open: true, row });
    setSerialInput(row.serials || []);
  };

  const handleSerialSave = () => {
    const row = serialModal.row;
    if ((serialInput || []).length !== Number(row.qty)) {
      Swal.fire(
        "Incorrect serial count",
        "Enter exact serials for qty",
        "warning"
      );
      return;
    }

    dispatch(
      updateItemRow({
        key: row.key,
        updates: { serials: serialInput },
      })
    );

    setSerialModal({ open: false, row: null });
  };

const focusQty = (key) => {
  setTimeout(() => {
    const row = document.querySelector(`tr[data-row-key="${key}"]`);
    const input = row?.querySelector(".ant-input-number input");
    if (input) {
      input.focus();
      input.select();
    }
  }, 150);  // slightly longer delay to let Redux state settle
};

const focusNextProduct = () => {
  setTimeout(() => {
    const inputs = document.querySelectorAll(
      ".ant-table-tbody .ant-select-selection-search-input"
    );
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus();
    }
  }, 150);
};

  useEffect(() => {
    if (cartMode !== "auto") return;

    const last = itemCart[itemCart.length - 1];

    // If no rows OR last row has item → add a fresh blank row
    if (!last || last.item_id) {
      dispatch(
        addItemRow({
          key: Date.now() + Math.random() * 10,
          item_id: null,
          product: "",
          qty: null,
          rate: null,
          discount: 0,
          tax_per: 0,
          unit_symbol: "",
          isBlank: true, // mark blank row
        })
      );
    }
  }, [itemCart, cartMode, dispatch]);

  const columns = [
    {
      title: "No.",
      dataIndex: "serial",
      key: "serial",
      width: 60,
      render: (text, record, index) => index + 1, // ✅ auto row number
    },
    // {
    //   title: "Product / Scan Barcode",
    //   dataIndex: "product",
    //   render: (_, record, index) => (
    //     <SearchListAutoComplete
    //       placeholder="Product / Scan Barcode"
    //       style={{ maxWidth: 180 }}
    //       rules={[{ required: true, message: "Select an Item" }]}
    //       value={{
    //         value: record.product || "",
    //         label: record.product || "",
    //         display_text: record.product || "",
    //       }}
    //       optionsList={(items || []).map((i) => ({
    //         sale_id: i.item_id,
    //         display_text: i.item_name,
    //         raw: i,
    //       }))}
    //       onFocus={() => dispatch(fetchItems())}
    //       allowFreeTyping={true}
    //       onEnter={(text) => {
    //         const txt = text.trim().toLowerCase();

    //         const match = items.find(
    //           (i) =>
    //             String(i.item_code).toLowerCase() === txt ||
    //             String(i.barcode).toLowerCase() === txt ||
    //             i.item_name.toLowerCase() === txt
    //         );

    //         addItemFromBarcode(match, record);
    //         focusQty(record.key);
    //       }}
    //       onSelect={(obj) => {
    //     // ✅ CLEAR (❌ button OR empty text)
    //     if (!obj) {
    //       dispatch(
    //         updateItemRow({
    //           key: record.key,
    //           updates: {
    //             item_id: null,
    //             product: "",
    //             qty: null,
    //             rate: null,
    //             discount: 0,
    //             tax_per: 0,
    //             unit_symbol: "",
    //             serials: [],
    //             is_serial: 0,
    //             isBlank: true,
    //           },
    //         })
    //       );
    //       dispatch(calculateTotals({ authInfo }));
    //       return;
    //     }

    //     // ✅ TYPING / BACKSPACE / DELETE
    //     if (!obj.raw) {
    //       const text = obj.display_text || "";

    //       // fully cleared → full reset
    //       if (text.trim() === "") {
    //         dispatch(
    //           updateItemRow({
    //             key: record.key,
    //             updates: {
    //               item_id: null,
    //               product: "",
    //               qty: null,
    //               rate: null,
    //               discount: 0,
    //               tax_per: 0,
    //               unit_symbol: "",
    //               serials: [],
    //               is_serial: 0,
    //               isBlank: true,
    //             },
    //           })
    //         );
    //         dispatch(calculateTotals({ authInfo }));
    //         return;
    //       }

    //       // typing only
    //       dispatch(
    //         updateItemRow({
    //           key: record.key,
    //           updates: { product: text },
    //         })
    //       );
    //       return;
    //     }

    //     // ✅ ACTUAL SELECTION
    //     addItemFromBarcode(obj.raw, record);
    //     focusQty(record.key);
    //   }}
    //       showAddNew={false}
    //     />
    //   ),
    // },
    {
  title: "Product / Scan Barcode",
  dataIndex: "product",
  render: (_, record, index) => (
    <div style={{ position: "relative" }}>
      <SearchListAutoComplete
        placeholder="Product / Scan Barcode"
        style={{ maxWidth: 180 }}
        rules={[{ required: true, message: "Select an Item" }]}
        value={{
          value: record.product || "",
          label: record.product || "",
          display_text: record.product || "",
        }}
       optionsList={(items || [])
  .filter((i) => i.is_service !== "yes")
  .filter((i) => i.is_asset !== "yes")
  .map((i) => ({
    sale_id: i.item_id,
    display_text: i.item_name,
    raw: i,
  }))
}
        onFocus={() => dispatch(fetchItems())}
        allowFreeTyping={true}

   onEnter={(text) => {
  console.log("📦 POS onEnter received:", text);
  console.log("📦 Length:", text?.length);
  const full = barcodeBuffer.current.length > text.trim().length
    ? barcodeBuffer.current
    : text.trim();

  barcodeBuffer.current = "";
  clearTimeout(barcodeTimer.current);
console.log("FULL BARCODE:", full);

  const txt = full.toLowerCase();
  const match = items.find(
    (i) =>
      String(i.item_code).toLowerCase() === txt ||
      String(i.item_barcode).toLowerCase() === txt ||
      i.item_name.toLowerCase() === txt
  );

    dispatch(updateItemRow({
    key: record.key,
    updates: { product: record.item_id ? record.product : "" },
  }));
  addItemFromBarcode(match || null, record);
focusNextProduct();  

}}

        onSelect={(obj) => {
          // ✅ CLEAR
          if (!obj) {
            dispatch(
              updateItemRow({
                key: record.key,
                updates: {
                  item_id: null,
                  product: "",
                  qty: null,
                  rate: null,
                  discount: 0,
                  tax_per: 0,
                  unit_symbol: "",
                  serials: [],
                  is_serial: 0,
                  isBlank: true,
                  itemInfo: null, // ✅ clear stock too
                },
              })
            );
            dispatch(calculateTotals({ authInfo }));
            return;
          }

          // ✅ TYPING
     // ✅ TYPING — accumulate into buffer, don't reset the row
if (!obj.raw) {
  const text = obj.display_text || "";
  console.log("⌨️ Typing value:", text);
  if (text.trim() === "") {
    barcodeBuffer.current = "";
    dispatch(updateItemRow({
      key: record.key,
      updates: {
        item_id: null, product: "", qty: null, rate: null,
        discount: 0, tax_per: 0, unit_symbol: "",
        serials: [], is_serial: 0, isBlank: true, itemInfo: null,
      },
    }));
    dispatch(calculateTotals({ authInfo }));
    return;
  }

  // Append to buffer (scanner types one char at a time)
barcodeBuffer.current += text;

console.log("BUFFER:", barcodeBuffer.current);
// AutoComplete gives cumulative value
  dispatch(updateItemRow({ key: record.key, updates: { product: text } }));
  return;
}

          // ✅ ACTUAL SELECTION
          const item = obj.raw;

          // 🔥 Fetch stock FIRST
          dispatch(
            fetchItemStock({
              itemId: item.item_id,
              branchId: authInfo.branch_id,
            })
          ).then((res) => {
          const stockItem = res?.payload?.find(
  (i) =>
    i.item_id === item.item_id &&
    i.is_service !== "yes" &&
    i.is_asset !== "yes"
);

const stock = Number(stockItem?.current_qty || 0);

            // ✅ Update row with stock + product
            dispatch(
              updateItemRow({
                key: record.key,
                updates: {
                  item_id: item.item_id,
                  product: item.item_name,
                  qty: 1,
                  rate: Number(item.sale_rate || 0).toFixed(2),
                  unit_symbol: item.unit_symbol,
                  discount: item.discount || 0,
                  tax_per: item.tax_per || 0,
                  itemInfo: { current_qty: stock }, // ✅ IMPORTANT
                  isBlank: false,
                },
              })
            );

            dispatch(calculateTotals({ authInfo }));
          });

          focusQty(record.key);
        }}

        showAddNew={false}
      />

      {/* ✅ AVAILABLE STOCK DISPLAY */}
      {record.item_id && (
        <span
          style={{
            position: "absolute",
            bottom: -18,
            left: 8,
            fontSize: 10,
            padding: "1px 6px",
            borderRadius: 4,
            background:
              record?.itemInfo?.current_qty <= 5
                ? "#fff1f0"
                : "#f5f5f5",
            color:
              record?.itemInfo?.current_qty <= 5
                ? "#cf1322"
                : "#595959",
            pointerEvents: "none",
          }}
        >
          Available Stock: {record?.itemInfo?.current_qty ?? 0}
        </span>
      )}
    </div>
  ),
},
    {
      title: "Serial",
      width: 80,
      render: (_, record) =>
        record.is_serial === "yes" || record.is_serial === 1 ? (
          <Button size="small" onClick={() => openSerialModal(record)}>
            Serial
          </Button>
        ) : (
          "-"
        ),
    },

    // {
    //   title: "Qty",
    //   dataIndex: "qty",
    //   width: 500,
    //   render: (_, record, index) =>
    //     cartMode === "manual" || record.item_id ? (
    //       <InputNumberField
    //         data-qty={record.key}
    //         min={1}
    //         precision={1}
    //         step={0.5}
    //         value={record.qty}
    //         // ref={el => qtyRefs.current[record.key] = el}
    //         onFocus={() => {
    //           handleUpdate(record.key, "qty", null);
    //         }}
    //         onBlur={() => {
    //           if (record.qty == null || record.qty === "") {
    //             handleUpdate(record.key, "qty", 1);
    //           }
    //         }}
    //         onChange={(v) => {
    //           handleUpdate(record.key, "qty", v);

    //           if (record.is_serial === "yes" || record.is_serial === 1) {
    //             openSerialModal(record);
    //           }
    //         }}
    //         onKeyDown={(e) => {
    //           if (e.key === "Enter") {
    //             if (taxMode === "on_item") {
    //               const input = document.querySelector(
    //                 `[data-discount="${record.key}"]`
    //               );
    //               input?.focus();
    //             } else {
    //               dispatch(
    //                 addItemRow({
    //                   key: Date.now() + Math.random(),
    //                   product: "",
    //                   qty: null,
    //                   rate: null,
    //                   discount: 0,
    //                   tax_per: 0,
    //                   unit_symbol: "",
    //                   isBlank: true,
    //                 })
    //               );
    //               focusNextProduct();
    //             }
    //           }
    //         }}
    //       />
    //     ) : (
    //       <span>{record.qty || 0}</span>
    //     ),
    // },
    {
  title: "Qty",
  dataIndex: "qty",
  width: 140,
  render: (_, record) =>
    cartMode === "manual" || record.item_id ? (
      <InputNumberField
        data-qty={record.key}
        min={1}
        precision={1}
        step={0.5}
        value={record.qty}
        style={{
          width: "100%",
          minWidth: 120,
        }}
        onFocus={() => {
          handleUpdate(record.key, "qty", null);
        }}
        onBlur={() => {
          if (record.qty == null || record.qty === "") {
            handleUpdate(record.key, "qty", 1);
          }
        }}
        onChange={(v) => {
          handleUpdate(record.key, "qty", v);

          if (record.is_serial === "yes" || record.is_serial === 1) {
            openSerialModal(record);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (taxMode === "on_item") {
              const input = document.querySelector(
                `[data-discount="${record.key}"]`
              );
              input?.focus();
            } else {
              dispatch(
                addItemRow({
                  key: Date.now() + Math.random(),
                  product: "",
                  qty: null,
                  rate: null,
                  discount: 0,
                  tax_per: 0,
                  unit_symbol: "",
                  isBlank: true,
                })
              );
              focusNextProduct();
            }
          }
        }}
      />
    ) : (
      <span>{record.qty || 0}</span>
    ),
},

    {
      title: "Rate",
      dataIndex: "rate",
      width: 120,
      render: (_, record) => <span>{Number(record.rate).toFixed(2)}</span>,
    },

    // Conditionally add VAT % column
    ...(taxMode === "on_item"
      ? [
          {
            title: "Discount %",
            dataIndex: "discount",
            width: 120,
            render: (_, record) =>
              cartMode === "manual" ? (
                <InputNumberField
                  min={0}
                  value={record.discount || 0}
                  onChange={(v) => handleUpdate(record.key, "discount", v)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      dispatch(
                        addItemRow({
                          key: Date.now() + Math.random(),
                          product: "",
                          qty: null,
                          rate: null,
                          discount: 0,
                          tax_per: 0,
                          unit_symbol: "",
                          isBlank: true,
                        })
                      );
                      focusNextProduct();
                    }
                  }}
                />
              ) : (
                <span>{record.discount || 0}</span> // ✅ show 0 before item selected
              ),
          },
          ...(showTax
            ? [
                {
                  title: `${taxLabel} %`,
                  dataIndex: "tax_per",
                  width: 120,
                  render: (_, record) => (
                    <span>{Number(record.tax_per || 0).toFixed(2)}</span>
                  ),
                },
              ]
            : []),
        ]
      : []),
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";

        const qty = new Big(r.qty || 0);
        const rate = new Big(r.rate || 0);
        let line = qty.times(rate);

        if (taxMode === "on_item") {
          const discountPct = new Big(r.discount || 0);
          const discountAmt = line.times(discountPct).div(100);
          const afterDiscount = line.minus(discountAmt);
          const taxPer = showTax ? new Big(r.tax_per || 0) : new Big(0);
          const taxAmt = afterDiscount.times(taxPer).div(100);
          line = afterDiscount.plus(taxAmt);
        }
        return line.toFixed(2);
      },
    },
  ];

  if (cartMode === "manual") {
    columns.push({
      title: "Action",
      render: (_, record) => (
        <Popconfirm
          title="Delete row?"
          onConfirm={() => dispatch(deleteItemRow(record.key))}
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    });
  }

  const flashRow = (key) => {
    setHighlightRow(key);
    setTimeout(() => setHighlightRow(null), 600);
  };

  return (
    <>
      <Flex justify="space-between" style={{ marginBottom: 10 }}>
        <div />
        {cartMode === "manual" && (
          <Button
            type="dashed"
            onClick={handleAddRow}
            icon={<PlusOutlined />}
            style={{ marginBottom: 8 }}
          >
            Add Row
          </Button>
        )}
      </Flex>
      <div style={{ width: "100%", maxWidth: "1300px" }}>
      <Table
        dataSource={itemCart}
        columns={columns}
        pagination={false}
        rowKey="key"
        scroll={{ x: "min-content" }}
          onRow={(record) => ({
    "data-row-key": record.key,   // ← add this
  })}
        rowClassName={(record) =>
          record.key === highlightRow ? "scan-highlight" : ""
        }
        footer={() => (
          <SharedFooter
            footerValues={footerValues}
            dataSource={itemCart}
            onFooterChange={handleFooterChange}
            pageType="pos"
            taxMode={taxMode}
          />
        )}
      />
      </div>
      {serialModal.open && (
        <Modal
          title="Enter Serial Numbers"
          open={serialModal.open}
          onCancel={() => setSerialModal({ open: false, row: null })}
          onOk={() => handleSerialSave()}
        >
          {Array.from({ length: serialModal.row.qty || 0 }).map((_, idx) => (
            <Input
              key={idx}
              placeholder={`Serial #${idx + 1}`}
              style={{ marginBottom: 8 }}
              value={serialInput[idx] || ""}
              onChange={(e) => {
                const updated = [...serialInput];
                updated[idx] = e.target.value;
                setSerialInput(updated);
              }}
            />
          ))}
        </Modal>
      )}
    </>
  );
};

export default PosTable;
